import { useEffect, useMemo, useRef, useState } from "react";
import type { RailItem } from "@/types/voice/resources-rail";

/** How long a chip stays lit after the Genie names it. */
const HIGHLIGHT_MS = 14000;

/** Labels shorter than this match too much ("FAQ", "Map") to be trustworthy. */
const MIN_LABEL_LENGTH = 5;

const normalise = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** "installation-manual.pdf" -> "installation manual" */
const fileNameStem = (fileName?: string): string | null => {
  if (!fileName) return null;
  const stem = fileName.replace(/\.[^.]+$/, "");
  return stem ? normalise(stem) : null;
};

interface TranscriptLike {
  message: string;
  isUser: boolean;
}

/**
 * Works out which resource the Genie just pointed the visitor at, so the rail
 * can light that chip and pin it first.
 *
 * This is deliberately a front-end read of the transcript rather than a
 * grounding signal from the backend: when the Genie says "it's on page 12 of
 * the installation manual", the visitor's eye should land on the installation
 * manual chip in the same beat. If the model later returns an explicit
 * citation, swap the matcher here and every caller keeps working.
 */
export const useSurfacedResource = (
  messages: TranscriptLike[],
  items: RailItem[],
): string | null => {
  const [surfacedId, setSurfacedId] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Only the latest Genie turn can surface something — matching the whole
  // history would re-light chips from ten minutes ago on every render.
  const latestAgentMessage = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (!messages[i].isUser) return messages[i].message ?? "";
    }
    return "";
  }, [messages]);

  const matchedId = useMemo(() => {
    if (!latestAgentMessage || items.length === 0) return null;
    const haystack = normalise(latestAgentMessage);
    if (!haystack) return null;

    // Longest label first, so "installation manual" wins over "manual".
    const candidates = items
      .map((item) => ({ item, needles: [normalise(item.label), fileNameStem(item.fileName)] }))
      .flatMap(({ item, needles }) =>
        needles
          .filter((needle): needle is string => !!needle && needle.length >= MIN_LABEL_LENGTH)
          .map((needle) => ({ id: item.id, needle })),
      )
      .sort((a, b) => b.needle.length - a.needle.length);

    return candidates.find(({ needle }) => haystack.includes(needle))?.id ?? null;
  }, [latestAgentMessage, items]);

  useEffect(() => {
    if (!matchedId) return;
    setSurfacedId(matchedId);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setSurfacedId(null), HIGHLIGHT_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [matchedId, latestAgentMessage]);

  return surfacedId;
};

export default useSurfacedResource;
